import {
  TypeWishlistCar,
  TypeWishlistExportOpinion,
  TypeWishlistCarModal,
  TypeWishListGalleryMedia,
  TypeCarUpdateEvent,
} from "@context/WishlistContext";
import { apiClient } from "./apiClient";
import {
  IWishlistQueryResponse,
  UserAddWishListPayload,
  UserGetWishListResponse,
  UserRemoveWishListPayload,
  WishListAPIResponseTemplate,
} from "@interfaces/wishlist.types";
import { fetchWishlistModelDetails } from "./queries/fetchModelForWishlist";
import getRetailTokenClient from "@utils/getRetailToken.client";
export interface WishlistResponse {
  cars: TypeWishlistCarModal[];
  totalCount: number;
}

export interface WishlistActionResponse {
  success: boolean;
  message: string;
  updatedWishlist?: TypeWishlistCar[];
}
export interface WishlistExportOpinionResponse {
  success: boolean;
  message: string;
  data?: TypeWishlistExportOpinion;
}

export interface WishlistGalleryResponse {
  success: boolean;
  message: string;
  data?: TypeWishListGalleryMedia[];
}
export interface WishListCarUpdateEventResponse {
  success: boolean;
  message: string;
  data?: TypeCarUpdateEvent[];
}
export interface WishlistCarModalResponse {
  success: boolean;
  message: string;
  wishlistCarModal?: TypeWishlistCarModal[];
}

export interface WishlistAssistanceResponse {
  success: boolean;
  message: string;
  data?: string[];
}

const mockExportOpinion: TypeWishlistExportOpinion[] = [
  {
    id: "",
    ratting: 8,
    text: "Forget the rest - <i>Kia Seltos is the best-balanced SUV in its class, with killer looks, a tech-loaded cabin, and solid performance.</i>",
    author: "Siddharth Patankar",
    authorRole: "Car expert",
    avatar: "/images/avatar1.png",
    highlightImage: "/images/image2.png",
    highlighText:
      "Kia Seltos blends style, features, and value in a compact SUV",
  },
];

const mockCarUpdateEventData: TypeCarUpdateEvent[] = [
  {
    type: "Price drop",
    text: "The price has been revised! You can now get it starting at ₹16.45 lakh",
    date: "24 May 2025",
  },
  {
    type: "New variant launch",
    text: "New Seltos variant HTX+ launched at just ₹14.4 lakh",
    date: "24 May 2025",
  },
];
const mockAssistList: string[] = [
  "Wondering which Kia Seltos variant is right for you?",
  "Buying the Kia Seltos? Let’s find one that fits your budget.",
  "Unsure about the color? We’ll help you pick one you’ll love driving every day.",
  "Curious how ACKO Drive offer better prices and quicker delivery?",
  "I have some other questions",
];
const fetchWishlistDetails = async (
  headers?: HeadersInit // Accept headers as an optional parameter
): Promise<WishListAPIResponseTemplate<UserGetWishListResponse>> => {
  try {
    const authHeaders = await getWishlistClientHeaders(headers);
    // Call your local API route instead of the backend directly
    const response = await apiClient.get(
      `${process.env.NEXT_PUBLIC_FRONTEND_HOST}/${process.env.NEXT_PUBLIC_BASE_TERM}api/wishlist/`, // Local API route
      {},
      { headers: authHeaders }
    );

    return response;
  } catch (e) {
    console.log(e);
    return {
      data: [] as UserGetWishListResponse,
      status: "error",
      message: "Something went wrong",
    };
  }
};

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};

  if (headers instanceof Headers) {
    const normalized: Record<string, string> = {};
    headers.forEach((value, key) => {
      normalized[key] = value;
    });
    return normalized;
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }

  return { ...headers };
};

const getWishlistClientHeaders = async (
  headers?: HeadersInit
): Promise<Record<string, string>> => {
  const retailToken = await getRetailTokenClient();

  return {
    ...normalizeHeaders(headers),
    ...(retailToken ? { RetailToken: retailToken } : {}),
  };
};

export const WishlistService = {
  fetchUserWishlist: async (
    headers?: HeadersInit
  ): Promise<WishListAPIResponseTemplate<UserGetWishListResponse>> => {
    const data = await fetchWishlistDetails(headers);
    return data;
  },

  addToWishlist: async (
    payload: UserAddWishListPayload,
    headers?: HeadersInit
  ): Promise<WishListAPIResponseTemplate<string>> => {
    try {
      const authHeaders = await getWishlistClientHeaders(headers);
      await apiClient.post(
        `${process.env.NEXT_PUBLIC_FRONTEND_HOST}/${process.env.NEXT_PUBLIC_BASE_TERM}api/wishlist/add/`,
        payload,
        { headers: authHeaders }
      );
      const updatedWishlistResponse = await fetchWishlistDetails(authHeaders);

      return {
        data: "Item added successfully.",
        status: "success",
        message: "",
        updatedWishlist: updatedWishlistResponse.data,
      };
    } catch (e) {
      return {
        data: "",
        status: "error",
        message: "Something went wrong",
      };
    }
  },
  removeFromWishlist: async (
    payload: UserRemoveWishListPayload,
    headers?: HeadersInit
  ): Promise<WishListAPIResponseTemplate<string>> => {
    try {
      const authHeaders = await getWishlistClientHeaders(headers);
      await apiClient.post(`/${process.env.NEXT_PUBLIC_BASE_TERM}api/wishlist/remove/`, payload, { headers: authHeaders });

      const updatedWishlistResponse = await fetchWishlistDetails(authHeaders);

      return {
        data: "Item removed successfully.",
        status: "success",
        message: "",
        updatedWishlist: updatedWishlistResponse.data,
      };
    } catch (e) {
      return {
        data: "",
        status: "error",
        message: "Something went wrong",
      };
    }
  },

  fetchModelDetails: async (payload: {
    slug: string;
    city: string;
  }): Promise<WishListAPIResponseTemplate<IWishlistQueryResponse>> => {
    try {
      const modelDetails = await fetchWishlistModelDetails(
        payload.slug,
        payload.city
      );
      return {
        data: modelDetails,
        status: "success",
        message: "Fetched",
      };
    } catch (e) {
      return {
        data: {} as IWishlistQueryResponse,
        status: "error",
        message: "Something went wrong",
      };
    }
  },

  updateMmvc: async (
    payload: {
      slug: string;
      productType: string;
      variantSlug: string;
      variantId: number;
      colorId: number;
    },
    headers?: HeadersInit
  ): Promise<WishListAPIResponseTemplate<string>> => {
    try {
      const authHeaders = await getWishlistClientHeaders(headers);
      await apiClient.post(
        `/${process.env.NEXT_PUBLIC_BASE_TERM}api/wishlist/update-mmvc/`,
        payload,
        { headers: authHeaders }
      );
      return {
        data: "MMVC updated successfully.",
        status: "success",
        message: "",
      };
    } catch (e) {
      return {
        data: "",
        status: "error",
        message: "Something went wrong",
      };
    }
  },

  rattingAndExportOpinion: async (
    slug: string
  ): Promise<WishlistExportOpinionResponse> => {
    return Promise.resolve({
      success: true,
      message: "ratting Export in wishlist",
      data: mockExportOpinion?.[0],
    });
  },

  updateEvent: async (slug: any): Promise<WishListCarUpdateEventResponse> => {
    return Promise.resolve({
      success: true,
      message: "update event in wishlist",
      data: mockCarUpdateEventData,
    });
  },

  assistList: async (slug: string): Promise<WishlistAssistanceResponse> => {
    return Promise.resolve({
      success: true,
      message: "assist in wishlist",
      data: mockAssistList,
    });
  },
};
